import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface GuestlineService {
  apiEndpointUrl: string;
  siteId: string;
  interfaceId: string;
  operatorCode: string;
  password: string;
  timezone: string;
  checkInTime: string;
  checkOutTime: string;
}

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    apiEndpointUrl: 'https://pmsws.eu.guestline.net/rlxsoaprouter/rlxsoap.asmx',
    siteId: '',
    interfaceId: '',
    operatorCode: '',
    password: '',
    timezone: '',
    checkInTime: '12:00',
    checkOutTime: '10:00',
  },
  features: {},
  cron: [{ name: 'guestline_reservation_sync', task: 'full_reservation_sync' }],
  webhooks: [],
});

export const guestline: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as GuestlineService;

    test(`${SERVICE}.apiEndpointUrl`, t('validations.required'), () => {
      enforce(service.apiEndpointUrl).isNotBlank();
    });

    omitWhen(!service.apiEndpointUrl, () => {
      test(`${SERVICE}.apiEndpointUrl`, t('validations.url'), () => {
        enforce(isEndpointUrl(service.apiEndpointUrl)).isTruthy();
      });
    });

    for (const field of [
      'siteId',
      'interfaceId',
      'operatorCode',
      'password',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });
    }

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });

    for (const field of ['checkInTime', 'checkOutTime'] as const) {
      omitWhen(!service[field], () => {
        test(`${SERVICE}.${field}`, t('validations.invalid'), () => {
          enforce(service[field]).matches(TIME_OF_DAY);
        });
      });
    }
  },
};
